import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import {
    AlarmsGridState,
    ContentAlarmsGrid,
    initialAlarmsGridState
} from '@/modules/configurationModule/detail/notifications/components/alarms-grid/alarms-grid.models'
import AlarmsModal
    from '@/modules/configurationModule/detail/notifications/components/alarms-modal/alarms-modal.vue'
import { alarmNotificationConfigurationServices } from '@/modules/configurationModule/detail/notifications/services/alarm-notification-services'
import { AlarmsModalState } from '@/modules/configurationModule/detail/notifications/components/alarms-modal/alarms-modal.models'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { StrictNullable } from '@/shared/types/app.types'
import { alarmTypeServices } from '@/modules/configurationModule/detail/notifications/services/alarm-type-services'
import { alarmsGridColumnDefs } from '@/modules/configurationModule/detail/notifications/components/alarms-grid/alarms-grid.column-defs'

@Component({
    components: {
        AlarmsModal,
        ConfigurationSectionHeader,
        VtGrid
    }
})
export default class AlarmsGrid extends BaseCrudServerSideComponent<ContentAlarmsGrid> implements BaseCrud {
    @Ref() readonly alarmsModalWrapper!: any
    @Ref() readonly alarmsModal!: any

    state: AlarmsGridState
    constructor() {
        super()
        this.state = initialAlarmsGridState()
    }

    async created(): Promise<void> {
        const alarmTypes = await this.$_toCallSecure(alarmTypeServices.getAllAlarmTypes()) ?? []
        this.state.columnsDef = alarmsGridColumnDefs(alarmTypes)
    }

    getContextMenuItems(params:GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$t('configuration.actions.update').toString(),
                cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                action: () => {
                    const nodeSelected:StrictNullable<RowNode> = params?.node
                    const selectedRow:ContentAlarmsGrid = nodeSelected?.data
                    return this.showModal(this.$t('configuration.actions.update').toString(), selectedRow, this.alarmsModalWrapper)
                }
            },
            {
                name: this.$t('configuration.actions.delete').toString(),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: () => this.deleteOperation(this.state.selectedRows)
            },
            {
                name: params?.node?.data.active ? this.$t('configuration.actions.deactivate').toString() : this.$t('configuration.actions.activate').toString(),
                cssClasses: this.state.selectedRows.length ? ['not-show'] : [''],
                action: () => this.activateDeactivateSelection(!params?.node?.data?.active, [params?.node?.data])
            },
            {
                name: this.$t('configuration.actions.activate').toString(),
                cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(true, this.state.selectedRows)
            },
            {
                name: this.$t('configuration.actions.deactivate').toString(),
                cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                action: () => this.activateDeactivateSelection(false, this.state.selectedRows)
            }
        ]
    }
    async activateDeactivateSelection(option: boolean, selectionBatch: ContentAlarmsGrid[]) {
        const alarmsToPatch = selectionBatch?.filter((alarm: ContentAlarmsGrid) => alarm.active !== option)?.map((alarm:ContentAlarmsGrid) => {
            return {
                ...alarm,
                active: option,
                customConfiguration: JSON.stringify(alarm.customConfiguration)
            }
        })
        if (alarmsToPatch.length) {
            this.$_updateLoading(true)
            const [err] = await this.$_toCall(alarmNotificationConfigurationServices.patchAlarmNotificationBatch(alarmsToPatch, this.state.laboratoryId))
            this.$_updateLoading(false)
            if (err) {
                return false
            }
            if (alarmsToPatch.length > 1) {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 2, { elements: alarmsToPatch.length }))
            } else {
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
            }
            this.reloadData()
        }
    }

    async createOperation(data: AlarmsModalState) {
        const alarmToCreate: any = {
            active: data.active,
            alarmType: data.generalConfiguration.alarmType?.code,
            cronExpression: data.generalConfiguration.cronExpression,
            customConfiguration: JSON.stringify(data.customConfiguration),
            initialWindowInMinutes: data.generalConfiguration.initialWindowInMinutes
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(alarmNotificationConfigurationServices.createAlarmNotification(alarmToCreate, this.state.laboratoryId))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$t('configuration.toastMessages.created')?.toString())
        this.alarmsModal.hide()
        this.reloadData()
    }

    async patchOperation(data: AlarmsModalState) {
        const alarmToPatch: any = {
            id: data.id,
            active: data.active,
            cronExpression: data.generalConfiguration.cronExpression,
            customConfiguration: JSON.stringify(data.customConfiguration),
            initialWindowInMinutes: data.generalConfiguration.initialWindowInMinutes
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(alarmNotificationConfigurationServices.patchAlarmNotificationBatch([alarmToPatch], this.state.laboratoryId))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response?.length) {
            this.$_handleErrors(response)
            return
        }
        this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', 1))
        this.alarmsModal.hide()
        this.reloadData()
    }

    async deleteBatchOperation(alarms: ContentAlarmsGrid[]) {
        const alarmsToDelete: EntityIdentifiersRequest = {
            entityIdentifiers: alarms.map((alarm: ContentAlarmsGrid) => Number(alarm?.id))
        }
        this.$_updateLoading(true)
        const [err, response] = await this.$_toCall(alarmNotificationConfigurationServices.deleteAlarmNotificationBatch(alarmsToDelete, this.state.laboratoryId))
        this.$_updateLoading(false)
        if (err) {
            this.$_showErrorModalFromErrorResponse(err)
            return
        } else if (response.length) {
            this.$_handleErrors(response)
            return
        }
        if (alarms.length > 1) {
            this.$_showToastSuccess(
                this.$tc('configuration.toastMessages.deleted', 2, { elements: alarms?.length })
            )
        } else {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', 1))
        }
        this.reloadData()
    }

    async deleteOperation(alarms: ContentAlarmsGrid[]) {
        if (alarms.length) {
            const selectedRowsSize:number = this.state?.selectedRows?.length
            const labelConfirmation:string = this.$t('configuration.grid.deleteConfirmation')?.toString()
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion:string = this.$t('configuration.grid.deleteQuestion')?.toString()
            const labelDeleteAction:string = this.$t('configuration.actions.delete')?.toString()
            const deleteConfirmation = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )
            if (deleteConfirmation) {
                this.deleteBatchOperation(alarms)
            }
        }
    }

    onUpdateButtonClick() {
        const first = this.state.gridApi?.getSelectedRows()[0]
        this.showModal(this.$t('configuration.actions.update').toString(), first, this.alarmsModalWrapper)
    }

    onDeleteButtonClick() {
        this.deleteOperation(this.state.selectedRows)
    }

    onAddButtonClick() {
        this.showModal(this.$t('configuration.actions.update').toString(), null, this.alarmsModalWrapper)
    }

    onActivateButtonClick() {
        this.activateDeactivateSelection(true, this.state.selectedRows)
    }

    onDeActivateButtonClick() {
        this.activateDeactivateSelection(false, this.state.selectedRows)
    }

    isUpdateButtonDisabled(): boolean {
        return this.state.gridApi?.getSelectedRows().length !== 1
    }

    isDeleteButtonDisabled(): boolean {
        return !this.state.gridApi?.getSelectedRows().length
    }
}
